import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Drawer, Empty, Input, notification, Pagination, Select, Spin, Tag } from 'antd';
import type { SelectProps } from 'antd/es/select';
import { SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import SelectCompanyMembers from '@src/components/Users/SelectCompanyMembers';
import { getUserListStatus } from '@src/packages/users/screens/UsersList/utils/getUserListStatus';
import { userHasAttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';
import type { AttendanceAddress } from '@src/packages/users/screens/UsersList/utils/userHasAttendanceAddress';
import { useIsMobile } from '@src/packages/utils';
import {
  getOrganizationCountry,
  needsJurisdictionIdentifier,
  type JurisdictionIdentifier,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import JurisdictionIdPromptModal from '@src/packages/workJourney/components/JurisdictionIdPromptModal';
import { JOURNEY_ASSIGNED_USERS_TYPE } from '@src/packages/workJourney/constants';
import { persistOrganizationUserJurisdictionId } from '@src/packages/workJourney/utils/persistOrganizationUserJurisdictionId';
import UserRepository from '@src/repositories/UserRepository';
import IntlMessages from '@src/util/IntlMessages';

import { useWithJourneyUsersPagination } from '../hooks/useWithJourneyUsersPagination';
import UsersSelectionDrawerAssignedList from './UsersSelectionDrawerAssignedList';

import drawerStyles from './usersSelectionDrawer.module.less';

const DEFAULT_PAGE_SIZE = 50;

const PaginationSizeSelect = (props: SelectProps<number>) => (
  <Select
    {...props}
    placement="topLeft"
    getPopupContainer={() => document.body}
  />
);

PaginationSizeSelect.Option = Select.Option;

type DrawerUser = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  info?: unknown;
  idCard?: string;
  jurisdictionId?: JurisdictionIdentifier;
  address?: AttendanceAddress;
  onboardingCompleted?: boolean;
  disabled?: boolean;
};

/** Some callers pass `{ user: DrawerUser }`; others pass a flat user object. */
export type DrawerSelectedUserInput = DrawerUser | { user: DrawerUser };

function unwrapDrawerUser(item: DrawerSelectedUserInput): DrawerUser {
  if (item && typeof item === 'object' && 'user' in item && item.user) {
    return item.user;
  }
  return item as DrawerUser;
}

const isSameDrawerUser = (left: DrawerUser, right: DrawerUser): boolean => {
  if (left.id && right.id) {
    return left.id === right.id;
  }

  if (left.email && right.email) {
    return left.email === right.email;
  }

  return false;
};

export type UsersSelectionDrawerProps = {
  isVisible: boolean;
  onCancel: () => void;
  onConfirm: (users: unknown[]) => void | Promise<void>;
  selectedUsers?: DrawerSelectedUserInput[];
  loading?: boolean;
  type?: typeof JOURNEY_ASSIGNED_USERS_TYPE.MEMBER | typeof JOURNEY_ASSIGNED_USERS_TYPE.SUPERVISOR;
  title: ReactNode;
  /** Secondary line under the drawer title (e.g. journey name). */
  description?: ReactNode;
  /** Required member count; when set, shows an assignment summary tag above search. */
  requiredMembers?: number;
  /** When set to a positive number, blocks adding more users than this limit. */
  maxSelections?: number;
  /** When set, loads only users assigned to this work shift (optional backend filter). */
  workShiftId?: string;
  /** When set, loads only users assigned to this work journey config (optional backend filter). */
  workJourneyConfigId?: string;
  /** When true, allows picking users who already have a work journey (document flows). */
  allowUsersWithExistingJourney?: boolean;
};

/**
 * @description Component drawer to select users from the organization
 */
const UsersSelectionDrawer = ({
  isVisible,
  onCancel,
  onConfirm,
  selectedUsers = [],
  loading = false,
  type = JOURNEY_ASSIGNED_USERS_TYPE.MEMBER,
  title,
  description,
  requiredMembers: requiredMembersTarget,
  maxSelections,
  workShiftId,
  workJourneyConfigId,
  allowUsersWithExistingJourney = false,
}: UsersSelectionDrawerProps) => {
  const intl = useIntl();
  const { isMobile } = useIsMobile();
  const userRepositoryRef = useRef(new UserRepository());
  const [search, setSearch] = useState('');
  const wasVisibleRef = useRef(false);
  const [localSelectedUsers, setLocalSelectedUsers] = useState<DrawerUser[]>(() =>
    selectedUsers.map(unwrapDrawerUser),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingJurisdictionUser, setPendingJurisdictionUser] =
    useState<DrawerUser | null>(null);
  const isSubmittingRef = useRef(false);
  const isSaving = loading || isSubmitting;
  const { orgId, organizationCountry } = useSelector(
    ({
      organizations,
    }: {
      organizations: {
        organization: OrganizationCountrySource & { id: string };
      };
    }) => ({
      orgId: organizations.organization.id,
      organizationCountry: getOrganizationCountry(organizations.organization),
    }),
  );

  const {
    allUsers,
    usersLoading,
    currentPage,
    pageSize,
    totalCount,
    handlePageChange,
    resetPagination,
  } = useWithJourneyUsersPagination({
    isVisible,
    organizationId: orgId,
    searchTerm: search,
    workShiftId,
    workJourneyConfigId,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const availableUsers = useMemo(() => {
    return (allUsers as DrawerUser[]).filter(
      user =>
        getUserListStatus(user) === 'active' &&
        !localSelectedUsers.some(selected => isSameDrawerUser(selected, user)),
    );
  }, [allUsers, localSelectedUsers]);

  const transformedUsers = useMemo(() => {
    return availableUsers.map(user => ({
      displayName: `${user.firstName} ${user.lastName}`,
      user: {
        ...user,
        info: user.info,
      },
      email: user.email,
      id: user.id,
    }));
  }, [availableUsers]);

  const effectiveMaxSelections =
    typeof maxSelections === 'number' && maxSelections > 0 ? maxSelections : undefined;

  const addUserToSelection = useCallback((userToAdd: DrawerUser) => {
    const { info: _info, ...userWithoutInfo } = userToAdd;
    setLocalSelectedUsers(prev => [...prev, userWithoutInfo]);
  }, []);

  const handleAddUser = useCallback(
    async userData => {
      if (isSaving) {
        return;
      }

      if (
        effectiveMaxSelections !== undefined &&
        localSelectedUsers.length >= effectiveMaxSelections
      ) {
        notification.warning({
          message: intl.formatMessage(
            { id: 'users.selectionDrawer.maxSelectionsReached' },
            { max: effectiveMaxSelections },
          ),
        });
        return;
      }

      const userToAdd = userData.user;
      const assignmentStatus = getUserListStatus(userToAdd ?? {});
      if (assignmentStatus === 'pending') {
        notification.warning({
          message: intl.formatMessage({ id: 'users.select.tooltip.pendingUser' }),
        });
        return;
      }
      if (assignmentStatus === 'inactive') {
        notification.warning({
          message: intl.formatMessage({ id: 'users.select.tooltip.inactiveUser' }),
        });
        return;
      }
      if (
        type === JOURNEY_ASSIGNED_USERS_TYPE.MEMBER &&
        !userHasAttendanceAddress(userToAdd?.address)
      ) {
        notification.warning({
          message: intl.formatMessage({ id: 'users.select.tooltip.missingAddress' }),
        });
        return;
      }
      if (userToAdd && typeof userToAdd === 'object') {
        if (type === JOURNEY_ASSIGNED_USERS_TYPE.MEMBER && userToAdd.id) {
          try {
            const orgUser = await userRepositoryRef.current.getUser(orgId, userToAdd.id);
            if (!orgUser) {
              throw new Error('Organization user not found');
            }
            const personalIdCard = orgUser.idCard || userToAdd.idCard || '';
            const jurisdictionId =
              orgUser.jurisdictionId ?? userToAdd.jurisdictionId;
            const resolvedUser = {
              ...userToAdd,
              address: orgUser.address ?? userToAdd.address,
              idCard: personalIdCard,
              jurisdictionId,
            };
            if (!userHasAttendanceAddress(resolvedUser.address)) {
              notification.warning({
                message: intl.formatMessage({ id: 'users.select.tooltip.missingAddress' }),
              });
              return;
            }
            if (
              needsJurisdictionIdentifier(
                organizationCountry,
                personalIdCard,
                jurisdictionId,
                orgUser.address?.country ?? userToAdd.address?.country,
              )
            ) {
              setPendingJurisdictionUser(resolvedUser);
              return;
            }
            addUserToSelection(resolvedUser);
            return;
          } catch (error) {
            console.error('Error loading organization user:', error);
            notification.error({
              message: intl.formatMessage({ id: 'general.loading.options.error' }),
            });
            return;
          }
        }
        addUserToSelection(userToAdd);
      } else {
        setLocalSelectedUsers(prev => [...prev, userToAdd]);
      }
    },
    [
      addUserToSelection,
      effectiveMaxSelections,
      intl,
      isSaving,
      localSelectedUsers.length,
      orgId,
      organizationCountry,
      type,
    ],
  );

  const handleConfirm = useCallback(async () => {
    if (isSubmittingRef.current || loading) {
      return;
    }

    const users = localSelectedUsers.map(user => ({
      organizationId: orgId,
      user,
    }));

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      await Promise.resolve(onConfirm(users));
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [loading, localSelectedUsers, onConfirm, orgId]);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      if (isSaving) {
        return;
      }

      setLocalSelectedUsers(current => current.filter(user => user.id !== userId));
    },
    [isSaving],
  );

  const handleCancel = useCallback(() => {
    if (isSaving) {
      return;
    }

    setLocalSelectedUsers(selectedUsers.map(unwrapDrawerUser));
    setSearch('');
    resetPagination();
    onCancel();
  }, [isSaving, onCancel, resetPagination, selectedUsers]);

  useEffect(() => {
    if (!isVisible) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setPendingJurisdictionUser(null);
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && !wasVisibleRef.current) {
      setSearch('');
    }
    wasVisibleRef.current = isVisible;

    if (isVisible) {
      setLocalSelectedUsers(selectedUsers.map(unwrapDrawerUser));
    }
  }, [isVisible, selectedUsers]);

  const showPagination = totalCount > 0;

  const searchPlaceholder = intl.formatMessage({
    id: 'workJourney.users.drawer.searchPlaceholder',
  });

  const showAssignmentSummary =
    typeof requiredMembersTarget === 'number' && requiredMembersTarget > 0;
  const isUnderstaffed = showAssignmentSummary && localSelectedUsers.length < requiredMembersTarget;

  const paginationFooter = showPagination ? (
    <Pagination
      className={drawerStyles.pagination}
      current={currentPage}
      onChange={handlePageChange}
      onShowSizeChange={handlePageChange}
      pageSize={pageSize}
      total={totalCount}
      simple={isMobile}
      size={isMobile ? 'small' : 'default'}
      showSizeChanger={!isMobile}
      selectComponentClass={PaginationSizeSelect}
      showQuickJumper={false}
      disabled={usersLoading || isSaving}
      pageSizeOptions={['10', '20', '50', '100']}
    />
  ) : null;

  const drawerTopInner = (
    <>
      <UsersSelectionDrawerAssignedList
        users={localSelectedUsers}
        onRemoveUser={handleRemoveUser}
        disabled={isSaving}
      />

      {showAssignmentSummary ? (
        <div className={drawerStyles.assignmentSummary}>
          <Tag
            className={classNames(
              drawerStyles.assignmentBadge,
              isUnderstaffed
                ? drawerStyles.assignmentBadgeUnderstaffed
                : drawerStyles.assignmentBadgeComplete,
            )}
          >
            <IntlMessages
              id="workJourney.users.drawer.assignedSummary"
              values={{
                assigned: localSelectedUsers.length,
                required: requiredMembersTarget,
              }}
            />
          </Tag>
          {isUnderstaffed ? (
            <span className={drawerStyles.summaryHint}>
              <IntlMessages id="workJourney.users.drawer.understaffedHint" />
            </span>
          ) : null}
        </div>
      ) : null}

      <div className={drawerStyles.searchSection}>
        <Input
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder={searchPlaceholder}
          value={search}
          onChange={event => setSearch(event.target.value)}
          className={drawerStyles.searchInput}
          aria-label={searchPlaceholder}
          disabled={isSaving}
        />
      </div>
    </>
  );

  const drawerBody = usersLoading ? (
    <div className={drawerStyles.tableState}>
      <Spin />
    </div>
  ) : transformedUsers.length === 0 ? (
    <Empty
      className={drawerStyles.emptyResults}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        search.trim()
          ? intl.formatMessage({ id: 'users.selectionDrawer.emptySearch' })
          : intl.formatMessage({ id: 'users.selectionDrawer.emptyAvailable' })
      }
    />
  ) : (
    <SelectCompanyMembers
      onAdd={handleAddUser}
      data={transformedUsers}
      type={type}
      allowUsersWithExistingJourney={allowUsersWithExistingJourney}
      maxHeight="100%"
      stickyHeader
      hostClassName={drawerStyles.tableHost}
      contentClassName={drawerStyles.tableHostContent}
      tableWrapperClassName={drawerStyles.tableWrapper}
      tableClassName={drawerStyles.drawerTable}
    />
  );

  return (
    <Drawer
      open={isVisible}
      closable={false}
      destroyOnClose
      footer={null}
      width={isMobile ? '100%' : 680}
      title={null}
      onClose={handleCancel}
      className={drawerStyles.drawer}
    >
      <div className={drawerStyles.shell}>
        <BoxContainer
          content
          fixed
          bottomBorder
          className={drawerStyles.header}
          contentClassName={drawerStyles.headerInner}
        >
          <FilterContainer
            className={drawerStyles.filterHeader}
            titleClassName={drawerStyles.headerTitleWrap}
            goBack={handleCancel}
            actionButtons={[
              {
                iconName: 'close',
                type: 'default',
                label: <IntlMessages id="button.cancel" />,
                action: handleCancel,
                disabled: isSaving,
              },
              {
                iconName: 'save',
                label: <IntlMessages id="button.save" />,
                type: 'primary',
                action: () => {
                  void handleConfirm();
                },
                disabled: isSaving || usersLoading,
                loading: isSaving,
              },
            ]}
            title={
              <div className={drawerStyles.headerTitleRow}>
                <Title.Header value={title} />
                {description ? (
                  <span className={drawerStyles.journeyBadge}>{description}</span>
                ) : null}
              </div>
            }
          />
        </BoxContainer>

        <div className={drawerStyles.content}>
          <div className={drawerStyles.topContent}>{drawerTopInner}</div>

          <div className={drawerStyles.tableSection}>
            <p className={drawerStyles.tableSectionHeader}>
              <IntlMessages id="workJourney.users.drawer.availableUsers" />
            </p>
            <div className={drawerStyles.tablePanel}>{drawerBody}</div>
          </div>

          {paginationFooter ? <div className={drawerStyles.footer}>{paginationFooter}</div> : null}
        </div>
      </div>
      <JurisdictionIdPromptModal
        open={!!pendingJurisdictionUser}
        organizationCountry={organizationCountry}
        personalCountry={pendingJurisdictionUser?.address?.country}
        personalIdCard={pendingJurisdictionUser?.idCard}
        userName={[
          pendingJurisdictionUser?.firstName,
          pendingJurisdictionUser?.lastName,
        ]
          .filter(Boolean)
          .join(' ')}
        onCancel={() => setPendingJurisdictionUser(null)}
        onConfirm={async jurisdictionId => {
          if (!pendingJurisdictionUser?.id) {
            return;
          }
          try {
            const orgUser = await userRepositoryRef.current.getUser(
              orgId,
              pendingJurisdictionUser.id,
            );
            if (!orgUser) {
              throw new Error('Organization user not found');
            }
            await persistOrganizationUserJurisdictionId(
              orgId,
              orgUser,
              jurisdictionId,
            );
            addUserToSelection({ ...pendingJurisdictionUser, jurisdictionId });
            setPendingJurisdictionUser(null);
          } catch (error) {
            console.error('Error saving jurisdiction identifier:', error);
            notification.error({
              message: intl.formatMessage({ id: 'general.save.error.message' }),
            });
          }
        }}
      />
    </Drawer>
  );
};

export default UsersSelectionDrawer;
